import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import MediaImage from "./MediaImage";
import { resolveThumbnailSrc, resolveVideoSrc } from "../lib/cloudinary";

const EMPTY_VIDEO_IDS = [];

export default function MediaVideo({
  publicId,
  cover,
  videoIds = EMPTY_VIDEO_IDS,
  className = "",
  aspect = "aspect-[16/9]",
  title = "Video",
  playButtonWhite = false,
  credit = null,
  autoplayOnView = false,
}) {
  const videoSrc = resolveVideoSrc(publicId);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const showCredit = Boolean(credit?.brand);

  const playBtnClass = playButtonWhite
    ? "group-hover:bg-white/10"
    : "group-hover:bg-[#F42B29] group-hover:border-[#F42B29]";

  useEffect(() => {
    if (autoplayOnView || !playing || !videoRef.current) return;
    videoRef.current.play().catch(() => {});
  }, [autoplayOnView, playing]);

  useEffect(() => {
    if (!autoplayOnView || !videoSrc || !containerRef.current) return;

    const video = videoRef.current;
    const container = containerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [autoplayOnView, videoSrc]);

  if (!videoSrc) {
    return (
      <div className={`relative overflow-hidden ${aspect} ${className}`} data-testid="media-video">
        <MediaImage
          publicId={publicId}
          cover={cover}
          videoIds={videoIds}
          alt={title}
          eager
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    );
  }

  if (autoplayOnView) {
    return (
      <div
        ref={containerRef}
        className={`relative overflow-hidden ${aspect} ${className}`}
        data-testid="media-video"
      >
        <video
          ref={videoRef}
          src={videoSrc}
          className="absolute inset-0 w-full h-full object-cover bg-black"
          muted
          loop
          playsInline
          preload="metadata"
          poster={resolveThumbnailSrc(publicId, cover, videoIds)}
          data-testid="media-video-player"
          aria-label={title}
        />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${aspect} ${className}`} data-testid="media-video">
      {playing ? (
        <video
          ref={videoRef}
          src={videoSrc}
          className="absolute inset-0 w-full h-full object-cover bg-black"
          controls
          autoPlay
          playsInline
          poster={resolveThumbnailSrc(publicId, cover, videoIds)}
          data-testid="media-video-player"
        />
      ) : (
        <button
          onClick={() => setPlaying(true)}
          className="absolute inset-0 group w-full h-full block text-left"
          data-testid="media-video-play"
          data-cursor="play"
          aria-label={`Play ${credit?.brand || title}`}
        >
          <MediaImage
            publicId={publicId}
            cover={cover}
            videoIds={videoIds}
            alt={credit?.brand || title}
            eager
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
          />

          {showCredit ? (
            <>
              <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/70 text-white font-body uppercase text-[10px] tracking-[0.2em] backdrop-blur-sm">
                <Play className="w-3 h-3" fill="currentColor" /> Film
              </span>
              <div
                className="absolute inset-0 transition-opacity duration-500 opacity-0 group-hover:opacity-90"
                style={{ backgroundColor: credit.color || "#F42B29" }}
              />
              <div className="absolute inset-0 z-10 p-6 md:p-10 flex flex-col justify-between text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="font-body uppercase text-xs tracking-[0.2em]">
                  <span>{credit.industry || "Film"}</span>
                </div>
                <div>
                  <h3 className="font-display text-4xl md:text-6xl uppercase tracking-mega leading-[0.9]">
                    {credit.brand}
                  </h3>
                  {credit.tagline ? (
                    <p className="mt-3 font-editorial italic text-base md:text-lg">{credit.tagline}</p>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`w-20 h-20 md:w-28 md:h-28 rounded-full border border-white flex items-center justify-center transition-colors duration-500 text-white ${playBtnClass}`}
                >
                  <Play className="w-7 h-7 md:w-10 md:h-10" fill="currentColor" />
                </span>
              </span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
